<template>
  <div>
    <h4>{{ $t('journal.search.title') }}</h4>
    <div class="py-3">
      <div class="searchSectionButtons">
        <span>{{ $t('journal.search.criterias.title') }} : </span>
        <v-btn plain text color="primary" @click="addCriteria(criterias.SEMANTIC_SEARCH)" small>
          {{ $t('journal.search.criterias.semantic.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.KEYWORD_SCORE)" small>
          {{ $t('journal.search.criterias.keywords.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.TIME_DECAY)" small>
          {{ $t('journal.search.criterias.timeDecay.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.URL_SCORE)" small>
          {{ $t('journal.search.criterias.url.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.PORTFOLIO_BIAS)" small>
          {{ $t('journal.search.criterias.portfolio.name') }}
        </v-btn>
      </div>
      <div class="searchSectionButtons">
        <span>{{ $t('journal.search.filters.title') }} : </span>
        <v-btn plain text color="primary" @click="addCriteria(criterias.KEYWORD_FILTER)" small>
          {{ $t('journal.search.filters.keywords.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.TIME_WINDOW_FILTER)" small>
          {{ $t('journal.search.filters.time.name') }}
        </v-btn>
        <v-btn plain text color="primary" @click="addCriteria(criterias.URL_FILTER)" small>
          {{ $t('journal.search.filters.url.name') }}
        </v-btn>
      </div>
    </div>
    <SearchCriteria
      @submit="submit"
      @remove-criteria="removeCriteria"
      v-for="crit in searchCriterias"
      :key="crit.key"
      :criteria="crit"
      :current-search-details="currentSearchDetails"
      ref="searchCriteriasRef"
    />
    <div class="d-flex justify-center pa-3 align-center">
      <v-text-field
        @keydown.enter.prevent="submit"
        :label="$t('journal.search.recommendations')"
        class="ma-2"
        hide-details
        dense
        outlined
        type="number"
        step="1"
        max="12"
        min="1"
        style="width: 150px; max-width: 150px;"
        v-model="recommendationCount"
      />
      <v-btn
        class="ma-2"
        color="primary"
        style="width: 150px; max-width: 150px;"
        @click="submit"
        elevation="0"
      >
        {{ $t('actions.search') }}
      </v-btn>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { nanoid } from 'nanoid';
import { NoosAssistantSearch } from './SearchUtils';
import SearchCriteria, { criterias } from './SearchCriteria';

export default {
  name: 'AdvancedSearch',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    existingSearchInfos: {
      type: Object,
      default: undefined,
    },
  },
  data: () => ({
    recommendationCount: 8,
    searchCriterias: [],
    criteriasOrder: [
      criterias.SEMANTIC_SEARCH,
      criterias.TIME_DECAY,
      criterias.KEYWORD_SCORE,
      criterias.PORTFOLIO_BIAS,
      criterias.KEYWORD_FILTER,
      criterias.TIME_WINDOW_FILTER,
      criterias.DATE_FILTER,
    ],
    criteriasInstancesCountByType: {},
  }),
  components: {
    SearchCriteria,
  },
  created() {
    this.defaultSections();
  },
  watch: {
    existingSearchInfos() {
      this.defaultSections();
    },
  },
  computed: {
    currentSearchDetails() {
      const currentSearchDetails = {
        criteriasInstancesCountByType: this.criteriasInstancesCountByType,
      };

      return currentSearchDetails;
    },
  },
  methods: {
    async defaultSections() {
      this.criterias = criterias;
      this.searchCriterias = [];

      if (!this.existingSearchInfos) return;
      if (this.existingSearchInfos.section) {
        try {
          const { searchId } = this.existingSearchInfos.section;
          await this.loadSavedSearch(searchId);
          this.recommendationCount = this.existingSearchInfos.section.recommendationCount;

          if (!this.recommendationCount) {
            this.recommendationCount = 8;
          }

          return;
        } catch (error) {
          this.criterias = criterias;
          this.searchCriterias = [];
          console.log(`Failed to load search ${this.existingSearchId}`, error);
        }
      }

      this.addCriteria(criterias.SEMANTIC_SEARCH);
      this.addCriteria(criterias.TIME_DECAY);
    },
    removeCriteria({ key, type }) {
      Vue.set(this.criteriasInstancesCountByType, type, this.criteriasInstancesCountByType[type] - 1);
      this.searchCriterias = this.searchCriterias.filter((c) => c.key !== key);
    },
    submit() {
      const query = {
        max_results: Number.parseInt(this.recommendationCount, 10),
        filters: [],
        scorings: [],
      };

      const rawCriterias = [];
      this.$refs.searchCriteriasRef.forEach((critRef) => {
        const criteria = critRef.getCriteria();
        rawCriterias.push(criteria);

        if (Array.isArray(criteria.criteria.filters)) {
          criteria.criteria.filters.forEach((i) => query.filters.push(i));
        }

        if (Array.isArray(criteria.criteria.scorings)) {
          criteria.criteria.scorings.forEach((i) => query.scorings.push(i));
        }
      });
      const preparedSearchable = new NoosAssistantSearch().fromQuery(query);
      this.$emit('submit', preparedSearchable);
    },
    addCriteria(type) {
      this.searchCriterias.push({ type, key: nanoid(10) });
      this.sortCriterias();

      if (this.criteriasInstancesCountByType[type] === undefined) {
        Vue.set(this.criteriasInstancesCountByType, type, 1);
      } else {
        Vue.set(this.criteriasInstancesCountByType, type, this.criteriasInstancesCountByType[type] + 1);
      }
    },
    sortCriterias() {
      this.searchCriterias.sort((a, b) => {
        const aa = this.getCriteriaDisplayOrder(a.type);
        const bb = this.getCriteriaDisplayOrder(b.type);
        if (aa > bb) return 1;
        if (aa < bb) return -1;
        return 0;
      });
    },
    getCriteriaDisplayOrder(type) {
      const criteriaOrderIndex = this.criteriasOrder.indexOf(type);
      return criteriaOrderIndex === -1 ? 999 : criteriaOrderIndex;
    },
    async loadSavedSearch(searchId) {
      const result = await new NoosAssistantSearch().loadExistingSearch(searchId);
      this.buildWidgetsFromSavedSearch(result);
      this.sortCriterias();
    },
    buildWidgetsFromSavedSearch(savedSearchData) {
      this.searchCriterias = [];

      const keysToSkip = ['max_results'];
      const keys = Object.keys(savedSearchData);
      const processedWidgets = [];

      keys.forEach(key => {
        if (keysToSkip.includes(key)) return;

        const elems = savedSearchData[key];
        elems.forEach(elem => {
          const elemKey = Object.keys(elem);
          const widgetId = elem[elemKey].id;
          if (processedWidgets.includes(widgetId)) {
            return;
          }
          processedWidgets.push(widgetId);

          const params = new NoosAssistantSearch().extractWidgetParamsFromSavedSearch(savedSearchData, widgetId, keysToSkip);
          const type = widgetId.split('/')[0];
          this.searchCriterias.push({ type, key: nanoid(10), params });
        });
      });
    },
  },
};
</script>
<style scoped>
.searchSectionButtons {
  text-align: left;
}
</style>
